import React, {memo, useMemo, useState} from 'react';
import moment, {Moment} from 'moment';
import {useTranslation} from 'react-i18next';
import {Box, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import {UserAvatar} from '../../../../../screens/private/office/professional-caregiver/planner/calendar/shared/user-avatar';
import {AffiliatedUser} from '../../../../../backend/responses/affiliated-user';
import {translations} from '../../../../../i18n/translations';
import {darkenColor, getContrastColor} from '../../../../../utils/functions/color-transforms';
import {createNewCareServiceItem} from '../../../base/data/care-service-data-transformation';
import {
  OnCareServiceCreatingItem,
  OnCareServiceEditItem,
  OnCareServiceClickItem,
  CareServiceItem,
} from '../../../base/data/care-service-item';
import './care-services-table.scss';

// ------------------------------------------------------------------------
//                         MAIN COMPONENT: TABLE
// ------------------------------------------------------------------------
type CareServicesTableProps = {
  children?: React.ReactNode[] | React.ReactNode;
  dataUpdating?: boolean;
  className?: string;
};

export function CareServicesTable(props: CareServicesTableProps) {
  return (
    <div
      className={`care-services-table ${props.className ?? ''}`}
      //className={`care-services-table ${props.dataUpdating ? 'updating' : ''} ${props.className ?? ''}`}}
      style={{
        cursor: props.dataUpdating ? 'wait' : undefined,
      }}
    >
      <Table
        stickyHeader
        style={{
          pointerEvents: props.dataUpdating ? 'none' : undefined,
        }}
      >
        {props.children}
      </Table>
    </div>
  );
}

// ------------------------------------------------------------------------
//                         TABLE HEADERS
// ------------------------------------------------------------------------
const TableHeaders = memo(function TableHeaders(props: {
  columns: DateRangeItem[];
  canWrite?: boolean;
  onCreatingItem: OnCareServiceCreatingItem;
}) {
  const {t} = useTranslation();

  return (
    <TableHead>
      <TableRow>
        <TableCell>
          <Typography className="caregiver-label">
            {t(translations.care_services.header.seniors_label)}
          </Typography>
        </TableCell>
        {props.columns.map((date) => (
          <TableCell key={date.id}>
            <div
              className="header-labels"
              style={{
                pointerEvents: props.canWrite ? undefined : 'none',
              }}
              onClick={() => {
                const now = moment().minute(0);
                const item = createNewCareServiceItem({
                  start: moment(date.id + 'T' + now.format('HH:mm')),
                  end: moment(date.id + 'T' + now.add(1, 'hour').format('HH:mm')),
                });
                props.onCreatingItem(item);
              }}
            >
              <Typography
                style={{
                  color: date.isToday ? 'var(--dcm-blue-base)' : 'var(--dcm-gray)',
                }}
              >
                {date.day}
              </Typography>
              <Typography
                style={{
                  color: date.isToday ? 'var(--dcm-blue-base)' : 'var(--dcm-gray)',
                  textTransform: date.isToday ? 'uppercase' : 'none',
                }}
              >
                {date.name}
              </Typography>
            </div>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
});

// ------------------------------------------------------------------------
//                         TABLE ROWS
// ------------------------------------------------------------------------
const TableRows = memo(function TableRows(props: {
  rows: Array<[AffiliatedUser, Map<DateRangeItem, CareServiceItem[]>]>;
  columns: DateRangeItem[];
  caregiversKV: Map<string, AffiliatedUser>;
  canWrite?: boolean;
  onCreatingItem: OnCareServiceCreatingItem;
  onClickItem: OnCareServiceClickItem;
  onEditItem: OnCareServiceEditItem;
}) {
  const [dragState, setDragState] = useState<DragState>(null);

  if (props.rows.length === 0) return null;

  // console.log('Table rows recalculated');

  const handleDropOnCell = (toSeniorId: string, toDateId: string) => {
    if (!dragState) return;

    const {item, sourceSeniorId, sourceDateId} = dragState;

    // Only move if the drop location is different from the source
    if (sourceSeniorId !== toSeniorId || sourceDateId !== toDateId) {
      const updatedItem = {...item}; // shallow copy
      // Update the target user and dates based on drop location
      updatedItem.targetUserId = toSeniorId;
      const newDate = moment(toDateId, 'YYYY-MM-DD');
      const newStartDate = newDate.clone().set({
        hour: item.startDate.hour(),
        minute: item.startDate.minute(),
        second: item.startDate.second(),
      });
      const newEndDate = newDate.clone().set({
        hour: item.endDate.hour(),
        minute: item.endDate.minute(),
        second: item.endDate.second(),
      });
      updatedItem.startDate = newStartDate;
      updatedItem.endDate = newEndDate;

      props.onEditItem(updatedItem);
    }

    setDragState(null);
  };

  return (
    <TableBody>
      {props.rows.map(([senior, cells]) => {
        return (
          <TableRow key={senior.user.id}>
            <TableCell style={{pointerEvents: props.canWrite ? undefined : 'none'}}>
              <Typography
                onClick={() => {
                  const item = createNewCareServiceItem({
                    start: moment().minute(0),
                    end: moment().add(1, 'hour').minute(0),
                  });
                  item.targetUserId = senior.user.id!;
                  props.onCreatingItem(item);
                }}
              >
                {senior.user.displayName || senior.user.name}
              </Typography>
            </TableCell>
            {props.columns.map((date) => {
              const items = cells.get(date);

              return (
                <TableCell
                  key={senior.user.id! + '.' + date.id}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('drag-over');
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove('drag-over');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('drag-over');
                    handleDropOnCell(senior.user.id!, date.id);
                  }}
                  data-drop-zone={`${senior.user.id}.${date.id}`}
                  onClick={
                    props.canWrite
                      ? () => {
                          const now = moment().minute(0);
                          const item = createNewCareServiceItem({
                            start: moment(date.id + 'T' + now.format('HH:mm')),
                            end: moment(date.id + 'T' + now.add(1, 'hour').format('HH:mm')),
                          });
                          item.targetUserId = senior.user.id!;
                          props.onCreatingItem(item);
                        }
                      : undefined
                  }
                >
                  <div className="cell-items">
                    {items?.map((item) => {
                      return (
                        <Row
                          key={item.id}
                          item={item}
                          caregivers={props.caregiversKV}
                          draggable={props.canWrite}
                          onClick={(evt) => {
                            evt.stopPropagation();
                            props.onClickItem(item);
                          }}
                          onDragStart={(dragItem, seniorId, dateId) => {
                            setDragState({
                              item: dragItem,
                              sourceSeniorId: seniorId,
                              sourceDateId: dateId,
                            });
                          }}
                          seniorId={senior.user.id!}
                          dateId={date.id}
                        />
                      );
                    })}
                  </div>
                </TableCell>
              );
            })}
          </TableRow>
        );
      })}
    </TableBody>
  );
});

function Row(props: {
  item: CareServiceItem;
  onClick?: (evt: React.MouseEvent<HTMLElement>) => void;
  onDragStart: (item: CareServiceItem, seniorId: string, dateId: string) => void;
  caregivers: Map<string, AffiliatedUser>;
  draggable?: boolean;
  seniorId: string;
  dateId: string;
}) {
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    props.onDragStart(props.item, props.seniorId, props.dateId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', props.item.id);
  };

  const caregivers = useMemo(() => {
    const list: AffiliatedUser[] = [];

    if (!props.item.responsibleUserIds) return list;

    for (const caregiverId of props.item.responsibleUserIds) {
      const caregiver = props.caregivers.get(caregiverId);
      if (caregiver) list.push(caregiver);
    }

    return list;
  }, [props.caregivers, props.item.responsibleUserIds]);

  const textColor = useMemo(() => getContrastColor(props.item.color), [props.item.color]);

  const borderColor = useMemo(() => darkenColor(props.item.color, 20), [props.item.color]);

  return (
    <Box
      className="cell-item"
      draggable={props.draggable}
      onDragStart={handleDragStart}
      style={{
        color: textColor,
        backgroundColor: props.item.color,
        borderLeft: '3px solid ' + borderColor,
      }}
      onClick={props.onClick}
    >
      <Typography className="cell-item-time">
        {props.item.startDate.format('HH:mm')} - {props.item.endDate.format('HH:mm')}
      </Typography>

      {caregivers.length > 0 && (
        <div className="cell-item-avatars">
          {caregivers.map((caregiver) => (
            <div className="cell-item-avatar" key={caregiver.user.id}>
              <UserAvatar
                name={caregiver.user.displayName || caregiver.user.name}
                size="s"
                sx={{width: 16, height: 16}}
                picture={caregiver.user.picture}
              />
              <Typography className="cell-item-name">
                {caregiver.user.displayName || caregiver.user.name}
              </Typography>
            </div>
          ))}
        </div>
      )}

      <Typography className="cell-item-title">{props.item.title}</Typography>
    </Box>
  );
}

// ------------------------------------------------------------------------
//                       EXPORT NICELY
// ------------------------------------------------------------------------
CareServicesTable.Header = TableHeaders;
CareServicesTable.Rows = TableRows;

// ------------------------------------------------------------------------
//                              HELPERS
// ------------------------------------------------------------------------
export type DateRangeItem = {
  id: string; // formatted as 'YYYY-MM-DD'
  day: string; // formatted as 'DD'
  name: string; // formatted as 'ddd' or 'Today'
  isToday?: boolean;
};

export function useDateRange(start: Moment, end: Moment): DateRangeItem[] {
  const {t} = useTranslation();

  return useMemo(() => {
    if (end.isBefore(start, 'day')) return [];
    // console.log('Date range recalculated');

    const days: DateRangeItem[] = [];
    let cursor = start.clone();
    const last = end.clone();
    const today = moment();

    while (cursor.isSameOrBefore(last, 'day')) {
      const isToday = cursor.isSame(today, 'day');
      days.push({
        id: cursor.format('YYYY-MM-DD'),
        day: cursor.format('DD'),
        name: isToday
          ? t(translations.office.professional_caregiver.shifts_allocations.table.today)
          : cursor.format('ddd'),
        isToday,
      });
      cursor.add(1, 'day');
    }

    return days;
  }, [start, end, t]);
}

// Drag state tracking
type DragState = {
  item: CareServiceItem;
  sourceSeniorId: string;
  sourceDateId: string;
} | null;
